import {
  parse,
  addDays,
  addMonths,
  startOfDay,
  endOfDay,
  differenceInDays,
  isWithinRange,
  isSameMonth } from 'date-fns'
import { loadScript, getLocaleDateStr } from '@/libs/utils'
import { getI18n } from '@/libs/i18n'

const CARD_TYPE = 'card'
const COUPON_TYPE = 'coupon'
const WIRE_TRANSFER_TYPE = 'wire transfer'

export const PLAN_NUMBER = {
  trial: 0,
  starter: 21,
  basic: 2,
  light: 22,
  standard: 3,
  premium: 23,
  pro: 4,
  trialOver: 100,
  terminated: 101,
  starterPlus: 4021,
  basicPlus: 4002,
  lightPlus: 4022,
  standardPlus: 4003,
  premiumPlus: 4023,
  proPlus: 4004,
  cloud1M: 1001,
  cloud2M: 1002,
  cloud3M: 1003,
  cloud4M: 1004,
  cloud5M: 1005,
  cloud1MPlus: 5001,
  cloud2MPlus: 5002,
  cloud3MPlus: 5003,
  cloud4MPlus: 5004,
  cloud5MPlus: 5005
}

export const AB_PLAN_NUMBER = 3000

export const PLAN_NAME = {
  basic: 'Basic',
  standard: 'Standard',
  pro: 'Pro'
}

function getServicePlanNumber (planNumber) {
  // todo: jp, en plan
  switch (planNumber) {
    case PLAN_NUMBER.trial:
      return PLAN_NUMBER.pro
    default:
      return planNumber
  }
}

export function getPlanFeature (planNumber) {
  const options = {
    weeklyReport: false,
    bLogin: false,
    funnels: false,
    sessionReport: false,
    liveHeatmapPdf: false
  }

  if (planNumber === undefined || planNumber === null) {
    return options
  }

  if ([PLAN_NAME.trialOver || PLAN_NUMBER.terminated].includes(planNumber)) {
    return options
  }

  options.weeklyReport = true
  options.bLogin = true
  options.funnels = true
  options.sessionReport = true
  options.liveHeatmapPdf = true

  return options
}

export const normalPlans = [
  {
    planNumber: PLAN_NUMBER.trial,
    name: 'Trial',
    pv: 10000,
    price: 0
  },
  {
    planNumber: PLAN_NUMBER.starter,
    name: 'Starter',
    pv: 50000,
    price: 60000,
    enPrice: 90,
    jaPrice: 9000
  },
  {
    planNumber: PLAN_NUMBER.basic,
    name: 'Basic',
    pv: 100000,
    price: 100000,
    enPrice: 200,
    jaPrice: 15000
  },
  {
    planNumber: PLAN_NUMBER.light,
    name: 'Light',
    pv: 200000,
    price: 200000,
    enPrice: 400,
    jaPrice: 30000
  },
  {
    planNumber: PLAN_NUMBER.standard,
    name: 'Standard',
    pv: 300000,
    price: 300000,
    enPrice: 600,
    jaPrice: 45000
  },
  {
    planNumber: PLAN_NUMBER.premium,
    name: 'Premium',
    pv: 400000,
    price: 400000,
    enPrice: 800,
    jaPrice: 60000
  },
  {
    planNumber: PLAN_NUMBER.pro,
    name: 'Pro',
    pv: 500000,
    price: 500000,
    enPrice: 1000,
    jaPrice: 75000
  },
  {
    planNumber: PLAN_NUMBER.starterPlus,
    name: 'Starter+',
    pv: 50000,
    price: 90000,
    enPrice: 135,
    jaPrice: 13500
  },
  {
    planNumber: PLAN_NUMBER.basicPlus,
    name: 'Basic+',
    pv: 100000,
    price: 150000,
    enPrice: 300,
    jaPrice: 22500
  },
  {
    planNumber: PLAN_NUMBER.lightPlus,
    name: 'Light+',
    pv: 200000,
    price: 300000,
    enPrice: 600,
    jaPrice: 45000
  },
  {
    planNumber: PLAN_NUMBER.standardPlus,
    name: 'Standard+',
    pv: 300000,
    price: 450000,
    enPrice: 900,
    jaPrice: 67500
  },
  {
    planNumber: PLAN_NUMBER.premiumPlus,
    name: 'Premium+',
    pv: 400000,
    price: 600000,
    enPrice: 1200,
    jaPrice: 90000
  },
  {
    planNumber: PLAN_NUMBER.proPlus,
    name: 'Pro+',
    pv: 500000,
    price: 750000,
    enPrice: 1500,
    jaPrice: 112500
  },
  {
    planNumber: PLAN_NUMBER.cloud1M,
    name: 'Cloud 1M',
    pv: 1000000,
    price: 1000000,
    enPrice: 2000,
    jaPrice: 150000
  },
  {
    planNumber: PLAN_NUMBER.cloud2M,
    name: 'Cloud 2M',
    pv: 2000000,
    price: 2000000,
    enPrice: 4000,
    jaPrice: 300000
  },
  {
    planNumber: PLAN_NUMBER.cloud3M,
    name: 'Cloud 3M',
    pv: 3000000,
    price: 3000000,
    enPrice: 6000,
    jaPrice: 450000
  },
  {
    planNumber: PLAN_NUMBER.cloud4M,
    name: 'Cloud 4M',
    pv: 4000000,
    price: 4000000,
    enPrice: 8000,
    jaPrice: 600000
  },
  {
    planNumber: PLAN_NUMBER.cloud5M,
    name: 'Cloud 5M',
    pv: 5000000,
    price: 5000000,
    enPrice: 10000,
    jaPrice: 7500000
  },
  {
    planNumber: PLAN_NUMBER.cloud1MPlus,
    name: 'Cloud 1M+',
    pv: 1000000,
    price: 1500000,
    enPrice: 3000,
    jaPrice: 225000
  },
  {
    planNumber: PLAN_NUMBER.cloud2MPlus,
    name: 'Cloud 2M+',
    pv: 2000000,
    price: 3000000,
    enPrice: 6000,
    jaPrice: 450000
  },
  {
    planNumber: PLAN_NUMBER.cloud3MPlus,
    name: 'Cloud 3M+',
    pv: 3000000,
    price: 4500000,
    enPrice: 9000,
    jaPrice: 675000
  },
  {
    planNumber: PLAN_NUMBER.cloud4MPlus,
    name: 'Cloud 4M+',
    pv: 4000000,
    price: 6000000,
    enPrice: 12000,
    jaPrice: 900000
  },
  {
    planNumber: PLAN_NUMBER.cloud5MPlus,
    name: 'Cloud 5M+',
    pv: 5000000,
    price: 7500000,
    enPrice: 15000,
    jaPrice: 1125000
  }
].map(item => {
  return {
    ...item,
    ...getPlanFeature(item.planNumber)
  }
})

const normalPlanMap = {}

for (const item of normalPlans) {
  normalPlanMap[item.planNumber] = item
}

export function getNormalPlanData (planNumber) {
  return normalPlanMap[planNumber]
}

const planOptionNames = [
  'pv',
  'dataReflection',
  'availablePages',
  'dataStoragePeriod',
  'heatmap',
  'analytics',
  'comparing',
  'cta',
  'abTesting',
  'excludeCertainElement',
  'excludeCertainIp',
  'weeklyReport',
  'bLogin',
  'funnels',
  'sessionReport',
  'liveHeatmap',
  'liveHeatmapPdf'
]

export const normalPlanOptionList = planOptionNames.map(optionName => {
  let defaultValue

  switch (optionName) {
    case 'dataReflection':
      defaultValue = 1
      break
    case 'availablePages':
      defaultValue = 'Unlimited'
      break
    case 'dataStoragePeriod':
      defaultValue = 6
      break
    case 'heatmap':
    case 'analytics':
    case 'comparing':
    case 'cta':
    case 'abTesting':
    case 'excludeCertainElement':
    case 'excludeCertainIp':
    case 'liveHeatmap':
      defaultValue = true
      break
  }
  const options = {
    name: optionName,
    value: {}
  }

  for (const item of normalPlans) {
    let value = defaultValue

    if (value === undefined) {
      value = item[optionName]
    }
    options.value[item.planNumber] = value
  }

  return options
})

function isWireTransferPlan (plan) {
  const paymentType = plan.paymentType

  if (paymentType === WIRE_TRANSFER_TYPE) {
    return true
  } else if ([CARD_TYPE, COUPON_TYPE].includes(paymentType)) {
    return false
  } else if (plan.bFree) {
    return false
  }
  return false
}

function getNextPlanData (rawData, planNumber) {
  let nextPlan = rawData.nextPlan
  let data = null
  let planInfo = planNumber ? normalPlans.find(e => e.planNumber === planNumber) : null
  let nextPlanInfo = nextPlan && nextPlan.planNumber ? normalPlans.find(e => e.planNumber === nextPlan.planNumber) : null
  if (nextPlan) {
    data = {
      planName: nextPlan.planName,
      planNumber: nextPlan.planNumber,
      startDate: parse(nextPlan.useStartDate),
      endDate: parse(nextPlan.useEndDate),
      isCardPlan: nextPlan.paymentType === CARD_TYPE,
      isWireTransferPlan: isWireTransferPlan(nextPlan),
      isUpgrade: planInfo && nextPlanInfo && nextPlanInfo.pv > planInfo.pv,
      isDowngrade: planInfo && nextPlanInfo && nextPlanInfo.pv < planInfo.pv,
      pv: nextPlanInfo && nextPlanInfo.pv,
      isSameAsCurrent: planInfo && nextPlanInfo && nextPlanInfo.planNumber === planInfo.planNumber,
      referral: nextPlan.referral,
      isTerminated: nextPlan && nextPlan.planNumber === PLAN_NUMBER.terminated
    }
  }
  return data
}

export function getNormalizedPlanData (rawData) {
  const data = {
    hasPlan: false,
    isTrial: false,
    isTrialOver: false,
    isTerminated: false,
    isUnpaid: false,
    useCoupon: false,
    isCardPlan: false,
    isWireTransferPlan: false,
    planName: null,
    planNumber: null,
    servicePlanNumber: null,
    startDate: null,
    isLastMonthlyPlan: false,
    monthlyStartDate: null,
    monthlyEndDate: null,
    endDate: null,
    canCreate: false,
    canChange: false,
    canCancel: false,
    next: null,
    feature: getPlanFeature(null),
    pv: null,
    referral: null
  }

  if (!rawData || !rawData.currentPlan) {
    data.canCreate = true
    data.stickyData = getPlanStickyData(data)
    return data
  }
  const currentPlan = rawData.currentPlan
  const planNumber = currentPlan.planNumber

  if ([PLAN_NUMBER.terminated, PLAN_NUMBER.trialOver].includes(planNumber)) {
    if (planNumber === PLAN_NUMBER.terminated) {
      data.isTerminated = true
    } else if (planNumber === PLAN_NUMBER.trialOver) {
      data.isTrialOver = true
    }
    data.canCreate = true
    data.next = getNextPlanData(rawData)
    data.stickyData = getPlanStickyData(data)
    return data
  }

  data.next = getNextPlanData(rawData)
  data.referral = currentPlan.referral
  data.planName = currentPlan.planName
  data.planNumber = currentPlan.planNumber
  data.applyType = currentPlan.applyType
  data.servicePlanNumber = getServicePlanNumber(data.planNumber)
  data.startDate = parse(currentPlan.useStartDate)
  data.endDate = parse(currentPlan.useEndDate)

  const today = new Date()
  let monthlyStartDate = startOfDay(parse(data.startDate))
  let monthlyEndDate = endOfDay(addDays(addMonths(monthlyStartDate, 1), -1))

  if (isWithinRange(startOfDay(today), startOfDay(data.startDate), endOfDay(data.endDate))) {
    while (!isWithinRange(startOfDay(today), monthlyStartDate, monthlyEndDate)) {
      monthlyStartDate = addMonths(monthlyStartDate, 1)
      monthlyEndDate = endOfDay(addDays(addMonths(monthlyStartDate, 1), -1))
    }
  }

  data.isLastMonthlyPlan = isSameMonth(startOfDay(data.endDate), monthlyEndDate)

  if (monthlyEndDate > endOfDay(data.endDate)) {
    monthlyEndDate = endOfDay(data.endDate)
  }

  data.monthlyStartDate = monthlyStartDate
  data.monthlyEndDate = monthlyEndDate
  data.monthlyNextStartDate = startOfDay(addDays(data.monthlyEndDate, 1))

  if (currentPlan.paymentType === CARD_TYPE) {
    data.isCardPlan = currentPlan.paymentType === CARD_TYPE
  } else if (currentPlan.paymentType === WIRE_TRANSFER_TYPE) {
    data.isWireTransferPlan = isWireTransferPlan(currentPlan)
  } else if (currentPlan.paymentType === COUPON_TYPE) {
    data.useCoupon = true
  }

  if (planNumber === PLAN_NUMBER.trial) {
    data.hasPlan = true
    data.isTrial = true
    data.canCreate = true
  } else if (currentPlan.paymentStatus === 'unpaid') {
    data.hasPlan = true
    data.isUnpaid = true
    data.canCancel = true
  } else if (currentPlan.paymentType === COUPON_TYPE) {
    data.hasPlan = true
    data.canChange = true
    data.next = getNextPlanData(rawData, planNumber)

    if (data.next && data.next.isWireTransferPlan) {
      data.canChange = false
    }
  } else if (!currentPlan.bFree) {
    data.hasPlan = true
    data.next = getNextPlanData(rawData, planNumber)

    if (data.isCardPlan) {
      if (!data.next || data.next.isCardPlan) {
        data.canChange = true
        data.canCancel = true
      }
    }
  } else {
    throw new Error('invalid plan')
  }

  let planInfo = data.planNumber ? normalPlans.find(e => e.planNumber === data.planNumber) : null
  if (planInfo) {
    data.pv = planInfo.pv
  }

  data.feature = getPlanFeature(data.planNumber)
  data.stickyData = getPlanStickyData(data)
  return data
}

export function getPlanStickyData (planData) {
  let data = null
  const i18n = getI18n()

  if (!planData.hasPlan) {
    // 이용 중인 플랜 없는 경우 (기존 등록페이지 존재)
    data = {
      id: 'noPlan',
      title: i18n.t('payment.dialog.noPlan.title'),
      description: i18n.t('payment.dialog.noPlan.description'),
      buttonText: i18n.t('payment.dialog.noPlan.buttonText'),
      action: 'createPlan'
    }
    return data
  }

  const today = startOfDay(new Date())
  const startOfEndDate = startOfDay(planData.endDate)
  const expireRemains = differenceInDays(startOfEndDate, today)
  const expireDateString = getLocaleDateStr(planData.endDate)

  if (planData.isTrial) {
    if (expireRemains >= 0 && expireRemains <= 7) {
      // 트라이얼 만료 7일 전
      data = {
        id: 'trialExpire',
        title: i18n.t('payment.dialog.trialExpire.title', { expireDateString }),
        description: i18n.t('payment.dialog.trialExpire.description'),
        buttonText: i18n.t('payment.dialog.trialExpire.buttonText'),
        action: 'createPlan'
      }
    }
  } else if (planData.isUnpaid) {
    // Unpaid 플랜 상태
    data = {
      id: 'unpaid',
      title: i18n.t('payment.dialog.unpaid.title'),
      description: i18n.t('payment.dialog.unpaid.description'),
      buttonText: i18n.t('payment.dialog.unpaid.buttonText'),
      action: 'goBilling'
    }
  } else {
    if (planData.next && !planData.next.isTerminated) {
      if ((planData.next.isDowngrade || planData.next.isTerminated) && expireRemains >= 0 && expireRemains <= 30) {
        const changeDate = getLocaleDateStr(planData.next.startDate)

        if (planData.isCardPlan && planData.next.isCardPlan) {
          // 플랜 다운그레이드 신청 완료 (정기결제)
          data = {
            id: 'cardTypeDowngrade',
            title: i18n.t('payment.dialog.cardTypeDowngrade.title', {
              changeDate,
              nextPlanName: planData.next.planName
            }),
            description: i18n.t('payment.dialog.cardTypeDowngrade.description'),
            buttonText: i18n.t('payment.dialog.cardTypeDowngrade.buttonText'),
            action: 'cancelPlanChange'
          }
        } else if (planData.next.isWireTransferPlan) {
          data = {
            // 정기결제 이용중이나 다음달 무통장 적용 예정 (다운그레이드 플랜 적용)
            id: 'nonCaardTypeDowngrade',
            title: i18n.t('payment.dialog.nonCaardTypeDowngrade.title', {
              changeDate,
              nextPlanName: planData.next.planName
            }),
            description: i18n.t('payment.dialog.nonCaardTypeDowngrade.description'),
            buttonText: i18n.t('payment.dialog.nonCaardTypeDowngrade.buttonText'),
            action: 'contactUs'
          }
        }
      }
    } else {
      if (expireRemains >= 0 && expireRemains <= 30) {
        if (planData.useCoupon) {
          // 다음달 만료 예정 (쿠폰 사용자)
          data = {
            id: 'couponExpire',
            title: i18n.t('payment.dialog.couponExpire.title', { expireDateString }),
            description: i18n.t('payment.dialog.couponExpire.description'),
            buttonText: i18n.t('payment.dialog.couponExpire.buttonText'),
            action: 'changePlan'
          }
        } else if (planData.isCardPlan) {
          // 플랜 해지 30일 전 (정기결제)
          data = {
            id: 'cardTypeExpire',
            title: i18n.t('payment.dialog.cardTypeExpire.title', { expireDateString }),
            description: i18n.t('payment.dialog.cardTypeExpire.description'),
            buttonText: i18n.t('payment.dialog.cardTypeExpire.buttonText'),
            action: 'withdrawPlanCancellation'
          }
          if (planData.applyType === 'admin') {
            data.buttonText = i18n.t('payment.dialog.cardTypeExpire.adminPlanButtonText')
            data.action = 'contactUs'
          }
        } else if (planData.isWireTransferPlan) {
          // 다음달 만료 예정 (무통장)
          data = {
            id: 'nonCardTypeExpire',
            title: i18n.t('payment.dialog.nonCardTypeExpire.title', { expireDateString }),
            description: i18n.t('payment.dialog.nonCardTypeExpire.description'),
            buttonText: i18n.t('payment.dialog.nonCardTypeExpire.buttonText'),
            action: 'contactUs'
          }
        } else {
          // 다음달 만료 예정 (무통장)
          data = {
            id: 'nonCardTypeExpire',
            title: i18n.t('payment.dialog.nonCardTypeExpire.title', { expireDateString }),
            description: i18n.t('payment.dialog.nonCardTypeExpire.description'),
            buttonText: i18n.t('payment.dialog.nonCardTypeExpire.buttonText'),
            action: 'contactUs'
          }
        }
      }
    }
  }
  return data
}

export function loadPaymentScripts () {
  const files = [
    'https://code.jquery.com/jquery-1.12.4.min.js',
    'https://cdn.iamport.kr/js/iamport.payment-1.1.5.js'
  ]

  return loadScript(files[0]).then(() => {
    return loadScript(files[1])
  })
}

// 아임포트 연동아이디
const IAMPORT_ID = 'imp95788244'
let isReady = false

/* global IMP */
export function requestPayment (params) {
  return new Promise((resolve, reject) => {
    loadPaymentScripts().then(() => {
      if (!isReady) {
        IMP.init(IAMPORT_ID)
      }
      isReady = true
      IMP.request_pay(params, function (result) {
        if (result.success) {
          resolve({
            customerUid: params.customer_uid,
            data: result
          })
        } else {
          const error = new Error('payment failed')
          error.data = {
            customerUid: params.customer_uid,
            data: result
          }
          reject(error)
        }
      })
    }).catch(reject)
  })
}
